
import { defineComponent, ref, onMounted, onActivated } from "vue";
import { MBDatatable } from "magicbean-saas-common";
import addLogisticsServiceProvider from "@/views/organisation-and-facility/logistics-service-provider/AddLogisticsServiceProvider.vue";
import FilterDropdown from "@/views/organisation-and-facility/logistics-service-provider/FilterDropdown.vue";
import { MenuComponent } from "@/assets/ts/components";
import { ApiLogisticsServiceProvider } from "@/core/api";
import _ from "lodash";
import { useI18n } from "vue-i18n";
import { FilterOption, SortOption } from "@/core/directive/interface/common";
import { LogisticsServiceProviderTable } from "@/core/directive/interface/logisticsServiceProvider";

import store from "@/store";
import { formatDateTime, setModuleBCN } from "@/core/directive/function/common";
import { useRoute } from "vue-router";
import { Mutations } from "@/store/enums/StoreEnums";

export default defineComponent({
  name: "organisation-and-facility-logistics-service-provider",
  components: {
    MBDatatable,
    FilterDropdown,
    addLogisticsServiceProvider,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();

    const loading = ref(true);
    const tableData = ref<Array<LogisticsServiceProviderTable>>([]);
    const checkedCompanys = ref([]);
    const currentPage = ref<number>(1);
    const total = ref<number>(1);
    const pageSize = ref<number>(
      store.getters.currentConfigPageSize(route.name)
    );
    const search = ref<string>("");
    const filterOptions = ref<Array<FilterOption>>([]);
    const sortOptions = ref<Array<SortOption>>([
      { field: "id", direction: "desc" },
    ]);

    const tableHeader = ref([
      // {
      //   name: t("logisticsServiceProvider.id"),
      //   key: "id",
      //   sortable: true,
      // },
      {
        name: t("logisticsServiceProvider.name"),
        key: "service_provider_name",
        sortable: false,
      },
      {
        name: t("logisticsServiceProvider.shortName"),
        key: "service_provider_short_name",
        sortable: false,
      },
      {
        name: t("logisticsServiceProvider.shortKey"),
        key: "service_provider_short_key",
        sortable: false,
      },
      {
        name: t("logisticsServiceProvider.basedCountry"),
        key: "service_provider_based_country",
        sortable: false,
      },
      {
        name: t("logisticsServiceProvider.coverage"),
        key: "service_provider_coverage",
        sortable: false,
      },
      {
        name: t("logisticsServiceProvider.services"),
        key: "service_provider_services",
        sortable: false,
      },
      {
        name: t("logisticsServiceProvider.customsTerms"),
        key: "service_provider_customs_terms",
        sortable: false,
      },
      {
        name: t("logisticsServiceProvider.status"),
        key: "status",
        sortable: false,
      },
      {
        name: t("common.action"),
        key: "actions",
        sortable: false,
      },
    ]);

    const init = () => {
      MenuComponent.reinitialization();
      setModuleBCN(t, route);
      getLogisticsServiceProviderList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    onMounted(() => {
      if (store.getters.getAliveList.length === 0) {
        init();
      }
    });

    onActivated(() => {
      init();
    });

    const getLogisticsServiceProviderList = (
      page: number,
      pageSize: number,
      search?: string,
      filterOptions?: Array<FilterOption>,
      sortOptions?: Array<SortOption>
    ) => {
      loading.value = true;
      ApiLogisticsServiceProvider.getLogisticsServiceProviderList({
        page: page,
        page_size: pageSize,
        search_value: search,
        filter_group: filterOptions,
        sort_orders: sortOptions,
      })
        .then(({ data }) => {
          loading.value = false;
          if (data.code == 0) {
            tableData.value.splice(
              0,
              tableData.value.length,
              ...data.data.items
            );
            tableData.value.forEach((item) => {
              item.downloadDisabled = false;
            });
            total.value = data.data.total;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const updateList = () => {
      getLogisticsServiceProviderList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const onCurrentPageChange = (page: number) => {
      currentPage.value = page;
      getLogisticsServiceProviderList(
        page,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const onRowsPerPageChange = (pagesize: number) => {
      pageSize.value = pagesize;
      updateList();
      store.commit(Mutations.SET_CURRENT_PAGE_CONFIG, {
        key: route.name,
        data: {
          pageSize: pageSize.value,
        },
      });
    };

    const onColumnSort = (val) => {
      let sortObj: SortOption = {
        field: val.columnName,
        direction: val.order,
      };
      sortOptions.value.splice(0, sortOptions.value.length, sortObj);
      getLogisticsServiceProviderList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const handleFilter = (val) => {
      let filterArr: Array<FilterOption> = [];
      for (let item in val) {
        if (val[item] != "" && val[item] != null) {
          if (item == "service_provider_customs_terms") {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "in",
            });
          } else if (
            item == "service_provider_based_country" ||
            item == "service_provider_coverage" ||
            item == "service_provider_services" ||
            item == "service_provider_short_key"
          ) {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "eq",
            });
          } else {
            filterArr.push({
              field: item,
              value: val[item],
              condition: "like",
            });
          }
        }
      }
      filterOptions.value.splice(0, filterOptions.value.length, ...filterArr);
      search.value = "";
      currentPage.value = 1;
      getLogisticsServiceProviderList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const handleFilterReset = () => {
      resetFilter();
      getLogisticsServiceProviderList(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const resetFilter = () => {
      filterOptions.value.splice(0, filterOptions.value.length);
    };

    const debounceSearch = _.debounce(getLogisticsServiceProviderList, 1000);

    const searchItems = () => {
      currentPage.value = 1;
      debounceSearch(
        currentPage.value,
        pageSize.value,
        search.value,
        filterOptions.value,
        sortOptions.value
      );
    };

    const removeSearch = () => {
      search.value = "";
      currentPage.value = 1;
      updateList();
    };

    const statusClass = (status) => {
      let statusClass = "";
      switch (status) {
        case t("logisticsServiceProvider.new"):
        case t("logisticsServiceProvider.sellOnCredit"):
          statusClass = "badge-light-primary";
          break;
        case t("logisticsServiceProvider.processing"):
          statusClass = "badge-light-warning";
          break;
        case t("logisticsServiceProvider.partialShipped"):
        case "Shipped":
          statusClass = "badge-light-info";
          break;
        case t("logisticsServiceProvider.complete"):
          statusClass = "badge-light-success";
          break;
        case t("logisticsServiceProvider.refunded"):
          statusClass = "badge-light-danger";
          break;
        case t("logisticsServiceProvider.canceled"):
          statusClass = "badge-light-dark";
          break;
        default:
          statusClass = "badge-light-primary";
      }
      return statusClass;
    };

    const fileExport = (id, item) => {
      item.downloadDisabled = true;
      // ApiLogisticsServiceProvider.print({
      //   id: id,
      //   download_type: "stream",
      // })
      //   .then((data) => {
      //     console.log(data.headers["content-disposition"]);
      //     item.downloadDisabled = false;
      //     const disposition = data.headers["content-disposition"];
      //     fileDownload(data.data, disposition.split("filename=")[1]);
      //   })
      //   .catch((error) => {
      //     item.downloadDisabled = false;
      //     console.log(error);
      //   });
    };

    return {
      t,
      formatDateTime,
      loading,
      tableHeader,
      tableData,
      currentPage,
      total,
      pageSize,
      search,
      checkedCompanys,
      getLogisticsServiceProviderList,
      onCurrentPageChange,
      onRowsPerPageChange,
      onColumnSort,
      searchItems,
      removeSearch,
      handleFilter,
      handleFilterReset,
      updateList,
      statusClass,
      fileExport,
    };
  },
});
